import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Button, Center, Flex, Loader } from '@mantine/core';
import { useForm } from '@mantine/form';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { BOOKING_PASS_TYPES_KEYS } from '@/constant/booking';

import { GuestFields } from '@/entities/GuestFields/GuestFields.tsx';

import { bookingApi } from '@/shared/api/booking.ts';
import { useShowNotifaction } from '@/shared/hooks/useShowNotifaction.tsx';
import useAppStore from '@/shared/store/app.store.ts';
import { IRequestError } from '@/shared/types/app.types.ts';
import { IGuest } from '@/shared/types/booking.types';
import { NetworkError } from '@/shared/uikit/components/error/networkError';
import { LabelInput } from '@/shared/uikit/components/input';
import { DateTimeField } from '@/shared/uikit/components/picker/DateTimeField/DateTimeField.tsx';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { IconFileDescription, IconMessage, IconUser, IconUsers } from '@tabler/icons-react';

export const ExternalBooking = () => {
	const queryClient = useQueryClient();
	const { id } = useParams<{ id: string }>();
	const isMobile = useAppStore((state) => state.isMobile);
	const form = useForm<{ guests: IGuest[] }>({
		initialValues: {
			guests: [],
		},
		validate: {
			guests: (value) => {
				return value.some(
					(elem) =>
						!elem.full_name ||
						!elem.email ||
						((elem.pass_type === BOOKING_PASS_TYPES_KEYS.ENTRANCE_PARKING ||
							elem.pass_type === BOOKING_PASS_TYPES_KEYS.PARKING) &&
							!elem.car_number),
				);
			},
		},
	});

	const {
		data: externalBooking,
		isLoading: isLoadingExternalBooking,
		isError: isErrorLoadingExternalBooking,
	} = useQuery({
		queryKey: ['ExternalBooking', id],
		queryFn: () => bookingApi.getExternalBooking(id!),
		enabled: !!id,
	});

	const {
		mutate: addGuest,
		isPending: isAddingGuest,
		isError: isAddingGuestError,
	} = useMutation({
		mutationFn: bookingApi.addGuestToExternalBooking,
		onSuccess: () => {
			form.reset();
			void queryClient.invalidateQueries({ queryKey: ['ExternalBooking'] });
			useShowNotifaction({ text: 'Участники успешно добавлены', type: 'success' });
		},
		onError: (error: AxiosError<IRequestError>) => {
			form.reset();
			useShowNotifaction({
				text: error.response?.data?.message ?? '',
				type: 'error',
			});
		},
	});

	const handleAddGuest = () => {
		if (id === undefined) return;

		const guests = form.values.guests;
		addGuest({ bookingId: id, returnUrl: window.location.href, payload: guests });
	};

	const guests = useMemo(() => {
		if (!externalBooking) return [];

		return externalBooking.guests.map(({ full_name }) => full_name).join('\n');
	}, [externalBooking, isAddingGuest]);

	if (isLoadingExternalBooking) {
		return (
			<LayoutRow
				title={{
					title: { children: 'Приглашение на встречу', size: 'h3', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100dvh - 40px)', margin: 20, overflow: 'hidden' }}
			>
				<Center h="100%">
					<Loader />
				</Center>
			</LayoutRow>
		);
	}

	if (isErrorLoadingExternalBooking) {
		return (
			<LayoutRow
				title={{
					title: { children: 'Приглашение на встречу', size: 'h3', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100dvh - 40px)', margin: 20, overflow: 'hidden' }}
			>
				<NetworkError />{' '}
			</LayoutRow>
		);
	}

	if (isMobile) {
		return (
			<LayoutRow
				title={{
					title: { children: 'Приглашение на встречу', size: 'h3', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100dvh - 40px)', margin: 20, overflow: 'hidden' }}
			>
				<Flex gap={20} wrap="wrap" style={{ width: '100%', overflowY: 'auto', paddingBottom: 100 }}>
					<DateTimeField
						value={dayjs(externalBooking?.date).toDate()}
						valueFormat={'DD MMMM YYYY, HH:mm'}
						style={{ pointerEvents: 'none' }}
					/>
					<LabelInput
						label="Организатор"
						value={externalBooking?.organizer}
						rightSection={<IconUser opacity={0.5} />}
						style={{ pointerEvents: 'none' }}
						readOnly
					/>

					<LabelInput
						label="Тема"
						value={externalBooking?.subject}
						rightSection={<IconMessage opacity={0.5} />}
						style={{ pointerEvents: 'none' }}
						readOnly
					/>

					<LabelInput
						label="Описание"
						value={externalBooking?.body}
						rightSection={<IconFileDescription opacity={0.5} />}
						style={{ pointerEvents: 'none' }}
						multiple
						readOnly
					/>

					<LabelInput
						label="Участники"
						defaultValue={guests}
						rightSection={<IconUsers opacity={0.5} />}
						style={{ pointerEvents: 'none' }}
						multiple
						readOnly
					/>

					<Flex direction="column" gap={20} w="100%">
						<GuestFields
							form={form}
							isShowCarNumberField={externalBooking?.show_auto}
							isExternal={true}
						/>

						{!!form.values.guests.length && (
							<Button size="lg" onClick={handleAddGuest}>
								Сохранить
							</Button>
						)}
					</Flex>
				</Flex>
			</LayoutRow>
		);
	}

	return (
		<LayoutRow
			title={{
				title: { children: 'Приглашение на встречу', size: 'h3', fw: 800 },
			}}
			type="once"
			style={{ height: 'calc(100dvh - 40px)', margin: 20, overflow: 'hidden' }}
		>
			<Flex gap={20}>
				<Flex direction="column" w="40%" gap={20}>
					<Flex direction="column" gap={20} style={{ pointerEvents: 'none' }}>
						<Flex gap={20}>
							<DateTimeField
								value={dayjs(externalBooking?.date).toDate()}
								valueFormat={'DD MMMM YYYY, HH:mm'}
							/>
							<LabelInput
								label="Организатор"
								value={externalBooking?.organizer}
								rightSection={<IconUser opacity={0.5} />}
								readOnly
							/>
						</Flex>

						<LabelInput
							label="Участники"
							placeholder="Выбрать из списка"
							defaultValue={guests}
							rightSection={<IconUsers opacity={0.5} />}
							multiple
							readOnly
						/>
					</Flex>
					<GuestFields
						form={form}
						isShowCarNumberField={externalBooking?.show_auto}
						isExternal={true}
					/>
					{!!form.values.guests.length && (
						<Button size="lg" disabled={!form.isValid()} onClick={handleAddGuest}>
							Сохранить
						</Button>
					)}
				</Flex>

				<Flex direction="column" w="60%" gap={20} style={{ pointerEvents: 'none' }}>
					<LabelInput
						label="Тема"
						value={externalBooking?.subject}
						rightSection={<IconMessage opacity={0.5} />}
						readOnly
					/>
					<LabelInput
						label="Описание"
						value={externalBooking?.body}
						rightSection={<IconFileDescription opacity={0.5} />}
						multiple
						readOnly
					/>
				</Flex>
			</Flex>
		</LayoutRow>
	);
};
